import { IconName, useDisclosureWithContext, type CareOrder } from '@talos/kyoko';
import type { MenuItemDef } from 'ag-grid-community';
import { useCallback, useMemo, type ReactNode } from 'react';
import {
  CareOrderNettingPreviewDialog,
  CareOrderNettingPreviewMode,
  type CareOrderNettingPreviewDialogContext,
} from '../CareOrderNettingPreview/CareOrderNettingPreviewDialog';
import { getCareOrderCountLabel } from '../utils';
import { validateNetCareOrders } from '../validateNetCareOrders';

interface GetNetCareOrdersMenuItemParams<TCareOrder extends CareOrder> {
  selectedCareOrders: TCareOrder[];
}

interface NetCareOrdersActionState {
  disabled: boolean;
  tooltip?: string;
  action: () => void;
}

export const useNetCareOrdersMenuItem = <TCareOrder extends CareOrder>(): {
  getMenuItem: (params: GetNetCareOrdersMenuItemParams<TCareOrder>) => MenuItemDef | null;
  getButtonActionState: (params: GetNetCareOrdersMenuItemParams<TCareOrder>) => NetCareOrdersActionState;
  dialogs: ReactNode;
} => {
  const nettingPreviewDisclosure = useDisclosureWithContext<CareOrderNettingPreviewDialogContext>();
  const { open } = nettingPreviewDisclosure;

  const getButtonActionState = useCallback(
    ({ selectedCareOrders }: GetNetCareOrdersMenuItemParams<TCareOrder>): NetCareOrdersActionState => {
      const validationResult = validateNetCareOrders(selectedCareOrders);

      return {
        action: () => {
          if (!validationResult.nettable) {
            return;
          }

          open({
            mode: CareOrderNettingPreviewMode.Create,
            selectedCareOrders,
          });
        },
        disabled: !validationResult.nettable,
        tooltip: !validationResult.nettable ? validationResult.reason : undefined,
      };
    },
    [open]
  );

  const getMenuItem = useCallback(
    ({ selectedCareOrders }: GetNetCareOrdersMenuItemParams<TCareOrder>) => {
      if (selectedCareOrders.length === 0) {
        return null;
      }
      if (selectedCareOrders.some(careOrder => careOrder.isNetted)) {
        return null;
      }
      const buttonActionState = getButtonActionState({ selectedCareOrders });
      return {
        name: `Net ${getCareOrderCountLabel(selectedCareOrders.length)}`,
        action: buttonActionState.action,
        icon: `<i class="ag-icon ${IconName.ArrowsReduce}"/>`,
        disabled: buttonActionState.disabled,
        tooltip: buttonActionState.tooltip,
      } satisfies MenuItemDef;
    },
    [getButtonActionState]
  );

  const dialogs = useMemo(
    () => <CareOrderNettingPreviewDialog disclosure={nettingPreviewDisclosure} />,
    [nettingPreviewDisclosure]
  );

  return {
    getMenuItem,
    getButtonActionState,
    dialogs,
  };
};
